import { SubmissionErrors } from 'final-form';
import React from 'react';
import { Field, Form } from 'react-final-form';
import Select from 'react-select';
import {
  Button,
  CardBody,
  CardTitle,
  Col,
  Fade,
  Form as FormComponent,
  FormGroup,
  Label,
  Row,
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
} from 'reactstrap';

const RenderCheckbox = ({
  name,
  label,
  placeholder,
  disabled,
  initialValue,
}: {
  name: string;
  initialValue?: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <FormGroup row>
    <Label for={name} sm={4} md={3}>
      {label}
    </Label>
    <Col sm={8} md={9}>
      <Field<string>
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        initialValue={initialValue}
        type="checkbox">
        {({ input, meta, ...rest }) => (
          <Label>
            <input {...input} {...rest} type="checkbox" />
            <span
              className={
                meta.error ? 'text-danger visible' : 'text-danger invisible'
              }>
              {meta.error ? meta.error : 'All good'}
            </span>
          </Label>
        )}
      </Field>
    </Col>
  </FormGroup>
);

const RenderSelect = ({
  name,
  label,
  placeholder,
  disabled,
  options,
  loading,
  searchable,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  options?: { value: string; label: string }[];
}) => (
  <FormGroup row>
    <Label for={name} sm={4} md={3}>
      {label}
    </Label>
    <Col sm={8} md={9}>
      <Field<{ value: string; label: string }>
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        validate={(value: { value: string } | undefined) =>
          !value ? 'This field is required' : undefined
        }>
        {({ input, meta, ...rest }) => (
          <div>
            <Select
              {...input}
              {...rest}
              type="text"
              invalid={!!meta.error}
              options={options}
              isClearable
              isSearchable={searchable}
              isDisabled={disabled}
              isLoading={loading}
            />
            {
              <span
                className={
                  meta.error ? 'text-danger visible' : 'text-danger invisible'
                }>
                {meta.error ? meta.error : 'All good'}
              </span>
            }
          </div>
        )}
      </Field>
    </Col>
  </FormGroup>
);

interface PatientExportValues {
  exportType?: string;
  patientId?: string;
  organizationId?: string;
  questionnaireId?: string;
}

const PatientReportTabComponet = (props: {
  initialValues?: PatientExportValues;
  onSubmit: (
    values?: PatientExportValues,
  ) =>
    | SubmissionErrors
    | Promise<SubmissionErrors | undefined>
    | undefined
    | void;
}) => {
  return (
    <Fade>
      <CardBody>
        <CardTitle>
          <h3>Patient Reports</h3>
        </CardTitle>
        <Form onSubmit={props.onSubmit} initialValues={props.initialValues}>
          {({
            handleSubmit,
            form,
            submitting,
            pristine,
            hasValidationErrors,
          }) => (
            <Row>
              <Col>
                <FormComponent onSubmit={handleSubmit}>
                  <RenderSelect
                    name="export"
                    placeholder=" JSON / CSV / PDF"
                    label="Export"
                    options={[
                      { value: 'json', label: 'JSON' },
                      { value: 'csv', label: 'CSV' },
                      { value: 'pdf', label: 'PDF' },
                    ]}
                  />
                  <RenderSelect
                    name="organization"
                    placeholder="Pick a organization"
                    label="Organization"
                    options={[
                      { value: 'pow', label: 'Prince of Wales' },
                      { value: 'lpl', label: 'Liverpool' },
                      { value: 'stv', label: 'St Vin' },
                    ]}
                  />
                  <RenderSelect
                    name="patient"
                    placeholder="Pick a patient!"
                    label="Patient"
                    options={[
                      { value: '21', label: 'Sean Evins' },
                      { value: '22', label: 'Sean Pool' },
                      { value: '12', label: 'Sean Vin' },
                    ]}
                  />
                  <RenderCheckbox
                    name="singleQuestionnaire"
                    label="Single Questionnaire"
                  />
                  <Field name="singleQuestionnaire">
                    {({ input }) => {
                      if (input.value) {
                        return (
                          <RenderSelect
                            name="questionnaire"
                            placeholder="Pick a questionnaire"
                            label="Questionnaire"
                            options={[
                              { value: 'cristal', label: 'Cristal' },
                              { value: 'cdk', label: 'cdk' },
                              { value: 'cpod', label: 'cpod' },
                            ]}
                          />
                        );
                      }
                      return null;
                    }}
                  </Field>
                  <Col className="d-flex justify-content-end">
                    <Button
                      disabled={pristine}
                      className="mr-2"
                      color="warning"
                      onClick={() => form.reset()}>
                      Reset
                    </Button>
                    <Button color="success" disabled={hasValidationErrors}>
                      {submitting ? <Spinner size="sm" /> : 'Submit'}
                    </Button>
                  </Col>
                </FormComponent>
              </Col>
              <Col xs={3} md={3} xl={6} className="d-none d-md-block">
                <Toast className="w-100 m-auto">
                  <ToastHeader>Reactstrap</ToastHeader>
                  <ToastBody>
                    Hey, fill out this form to export your data!
                    {submitting ? (
                      <div className="text-center">
                        <Spinner></Spinner>
                      </div>
                    ) : null}
                  </ToastBody>
                </Toast>
              </Col>
            </Row>
          )}
        </Form>
      </CardBody>
    </Fade>
  );
};
const PatientReportTab = () => (
  <PatientReportTabComponet
    onSubmit={() => {
      return undefined;
    }}
  />
);
export default PatientReportTab;
